// src/components/ui/MostrarUsuario.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

const TIPO_LABEL = {
  adminGeneral: 'Administrador General',
  adminTec: 'Administrador Técnico',
  adminComercial: 'Administrador Comercial',
  bodeguero: 'Bodeguero',
  servicioCliente: 'Servicio al Cliente',
  despacho: 'Despacho de pedidos',
  particular: 'Cliente Particular',
  subDistribuidor: 'Sub Distribuidor',
  vendedor: 'Vendedor',
  mensajero: 'Mensajero',
};

function buildImageUrl(foto) {
  if (!foto) return null;
  if (/^(https?:|data:)/i.test(foto)) return foto;
  const path = foto.startsWith('/') ? foto : `/uploads/${foto}`;
  return `${API_BASE}${path}`;
}

function getInitialsFromFullName(nombreCompleto = '') {
  const parts = nombreCompleto.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0]?.[0] || '';
  const last  = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

const PALETTE = [
  '#FDE68A', '#FCA5A5', '#A7F3D0', '#93C5FD', '#C4B5FD',
  '#FBCFE8', '#6EE7B7', '#BFDBFE', '#DDD6FE', '#FFE4E6',
];
function hashStr(s) { let h = 0; for (let i=0;i<s.length;i++) h = (h*31 + s.charCodeAt(i)) | 0; return Math.abs(h); }
function pickColorKey(seed) { return PALETTE[hashStr(seed) % PALETTE.length]; }

const StatusBadge = ({ estado }) => {
  const active = String(estado) === 'activo';
  return (
    <span className={[
      'text-xs font-semibold px-2 py-0.5 rounded-full border',
      active ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
             : 'bg-rose-50 text-rose-700 border-rose-200'
    ].join(' ')}>
      {active ? 'Activo' : 'Inactivo'}
    </span>
  );
};

const MissingChip = () => (
  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
    Falta info
  </span>
);

const LineItem = ({ label, value, copyable=false }) => {
  const missing = value == null || value === '' || value === 'null' || value === 'undefined';
  const content = missing ? '—' : value;
  return (
    <div className="text-sm text-slate-700 flex items-center">
      <span className="text-slate-500 mr-1">{label}:</span>
      {copyable && !missing ? (
        <span className="font-medium break-all select-all">{content}</span>
      ) : (
        <span className="font-medium break-words">{content}</span>
      )}
      {missing && <MissingChip />}
    </div>
  );
};

export default function MostrarUsuario({ usuario, onEstadoCambiado }) {
  const {
    id,
    foto,
    nombreCompleto,
    empresa,
    direccion,
    whatsapp,
    nit,
    email,
    tipoUsuario,
    estado,
    fechaCreacion,
  } = usuario || {};

  const [estadoLocal, setEstadoLocal] = useState(estado || 'activo');
  const [loading, setLoading] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  const imgSrc        = useMemo(() => buildImageUrl(foto), [foto]);
  const initials      = useMemo(() => getInitialsFromFullName(nombreCompleto), [nombreCompleto]);
  const avatarColor   = useMemo(() => pickColorKey(`${id}|${email}|${nombreCompleto}`), [id, email, nombreCompleto]);
  const tituloTipo    = TIPO_LABEL[tipoUsuario] || (tipoUsuario || '—');
  const createdPretty = fechaCreacion ? new Date(fechaCreacion).toLocaleString() : null;

  const cambiarEstado = async () => {
    const actual = estadoLocal;
    const nuevo = actual === 'activo' ? 'inactivo' : 'activo';
    const confirm = window.confirm(
      actual === 'activo'
        ? '¿Seguro que deseas dar de baja a este usuario?'
        : '¿Deseas activar de nuevo este usuario?'
    );
    if (!confirm) return;

    // UI optimista
    setEstadoLocal(nuevo);
    setLoading(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const r = await fetch(`${API_BASE}/api/usuarios/${id}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ estado: nuevo }),
        signal: controller.signal,
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.msg || j?.error || r.statusText);

      onEstadoCambiado?.();
    } catch (e) {
      if (e.name === 'AbortError') {
        console.warn('[usuarios] PUT /estado abortado por timeout; dejando estado optimista.');
        onEstadoCambiado?.();
      } else {
        setEstadoLocal(actual);
        alert('Error al cambiar estado: ' + (e?.message || e));
      }
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full">
      {/* Header: avatar + nombre + estado */}
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          {imgOk && imgSrc ? (
            <img
              src={imgSrc}
              alt={nombreCompleto || 'Usuario'}
              className="w-20 h-20 rounded-full object-cover border border-slate-200"
              onError={() => setImgOk(false)}
            />
          ) : (
            <div
              className="w-20 h-20 rounded-full grid place-items-center text-slate-800 font-bold border border-slate-200"
              style={{ backgroundColor: avatarColor }}
              aria-label={nombreCompleto || 'Usuario'}
              title={nombreCompleto || 'Usuario'}
            >
              {initials}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900 truncate">
                {nombreCompleto || 'Usuario'}{(!nombreCompleto || nombreCompleto.trim() === '') && <MissingChip />}
              </h3>
              <p className="text-sm text-slate-600">
                {tituloTipo}
                {empresa ? <span className="text-slate-400"> · </span> : null}
                {empresa ? <span className="text-slate-700">{empresa}</span> : null}
                {!empresa && <MissingChip />}
              </p>
            </div>
            <StatusBadge estado={estadoLocal} />
          </div>
        </div>
      </div>

      {/* Datos */}
      <div className="mt-4 grid gap-2">
        <LineItem label="Correo"    value={email} copyable />
        <LineItem label="Dirección" value={direccion} />
        <LineItem label="WhatsApp"  value={whatsapp} />
        <LineItem label="NIT"       value={nit} />
        <LineItem label="Rol"       value={tituloTipo} />
        <LineItem label="Creado"    value={createdPretty} />
      </div>

      {/* Acciones */}
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Link
          to={`/editar-usuario/${id}`}
          className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-amber-300 bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600"
          title="Editar usuario"
        >
          Editar
        </Link>

        <button
          onClick={cambiarEstado}
          disabled={loading}
          className={[
            'inline-flex items-center justify-center px-3 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60',
            estadoLocal === 'activo'
              ? 'bg-rose-600 hover:bg-rose-700'
              : 'bg-emerald-600 hover:bg-emerald-700',
          ].join(' ')}
        >
          {loading
            ? 'Procesando…'
            : estadoLocal === 'activo'
              ? 'Dar de baja'
              : 'Activar'}
        </button>
      </div>
    </div>
  );
}
